// Constantes globales partagées client et serveur.
// Ce fichier ne doit contenir QUE des valeurs immuables.

export const APP_VERSION = '0.1.0'
export const APP_NAME = 'BD-SapoPro V2'

// Tarification — ABONNEMENT (décision du 10/06 ; modèle « paiement unique / lifetime » abandonné).
// Formule annuelle = 2 mois offerts. Clientes V1 déjà en place : 1 an offert puis abonnement.
export const PRICING = {
  starter: { label: 'Starter', monthlyEur: 15, yearlyEur: 150 },
  premium: { label: 'Premium', monthlyEur: 25, yearlyEur: 250 },
  // Module « cosmétique » vendu en ADD-ON (Lot 3). AXE DISTINCT des tiers ci-dessus (savon) :
  // ce n'est pas un PaidTier (cf. PAID_TIERS dans shared/schemas/abonnement.ts) mais un droit
  // « module actif » déverrouillé par un abonnement Stripe séparé. Annuel = 2 mois offerts.
  cosmetique: { label: 'Module Cosmétique', monthlyEur: 10, yearlyEur: 100 },
} as const

// ═══ MODULES SUSPENDUS (décision de Thérèse, 13/07/2026) ═══
// Un module listé ici est RETIRÉ DE L'INTERFACE pour TOUTES les clientes : ses tuiles du tableau
// de bord, ses entrées du menu ☰, sa carte de vente (Réglages) et sa tuile d'upsell disparaissent.
//
// POURQUOI UN SEUL DRAPEAU : masquer l'accès SANS arrêter la vente reviendrait à encaisser
// 10 €/mois pour un module invisible. Les trois écrans (Accueil, MenuNavigation, SectionAbonnement)
// lisent donc la MÊME liste — impossible d'en oublier un.
//
// RÉVERSIBLE EN UNE LIGNE : vider ce tableau réactive tout. AUCUN code n'a été supprimé — le
// formulateur, les lots cosmétiques, leurs routes serveur et le bouton « Demander un DIP » restent
// intacts, simplement inatteignables depuis l'interface.
//
// ⚠️ À NE PAS OUBLIER CÔTÉ STRIPE : si des clientes ont DÉJÀ souscrit un module suspendu, elles
// continueraient d'être prélevées sans y avoir accès. Résilier leurs abonnements.
//
// ═══ VIDÉ LE 20/07/2026 : le module cosmétique est ROUVERT ═══
// Il n'est plus suspendu, et il n'est plus vendu séparément : il est désormais INCLUS dans
// l'abonnement (cf. MODULES_INCLUS ci-dessous). Le drapeau reste en place pour un usage futur.
export const MODULES_SUSPENDUS: readonly string[] = []

/** Vrai si ce module est temporairement retiré de l'interface (vente comprise). */
export function estModuleSuspendu(module: string | undefined | null): boolean {
  return module != null && MODULES_SUSPENDUS.includes(module)
}

// ═══ MODULES INCLUS DANS L'ABONNEMENT (décision de Thérèse, 20/07/2026) ═══
// Un module listé ici est OUVERT À TOUTES LES ABONNÉES, sans supplément et sans figurer dans la
// colonne `modules` de leur compte : plus de vente add-on, plus de tuile d'upsell, plus de carte
// de souscription dans Réglages. C'est le pendant exact de MODULES_SUSPENDUS, à l'autre bout :
// l'un ferme pour tout le monde, l'autre ouvre pour tout le monde.
//
// POURQUOI UNE LISTE PLUTÔT QU'UNE SUPPRESSION DU VERROU : aucun code n'est supprimé. Les gardes
// serveur (exigeModule) et les filtres d'interface restent en place et continuent de fonctionner
// pour tout module qui redeviendrait payant. Retirer 'cosmetique' d'ici rétablit la vente à
// l'identique, sans rien réécrire.
//
// ⚠️ CÔTÉ STRIPE : le produit et les prix add-on restent définis (server/stripe/plans.ts), mais
// ils ne sont plus proposés nulle part et la route de souscription les REFUSE. Si des clientes
// avaient déjà souscrit le module, RÉSILIER leurs abonnements : elles paieraient un supplément
// pour un droit devenu gratuit.
export const MODULES_INCLUS: readonly string[] = ['cosmetique']

/** Vrai si ce module est offert à toutes les abonnées (aucun supplément à payer). */
export function estModuleInclus(module: string | undefined | null): boolean {
  return module != null && MODULES_INCLUS.includes(module)
}

/**
 * RÈGLE UNIQUE d'ouverture d'un module, partagée par le serveur (aModule) et par les trois
 * écrans (Accueil, MenuNavigation, SectionAbonnement) — impossible d'en oublier un.
 * Ordre : suspendu ferme pour tout le monde ; inclus ouvre pour tout le monde ; sinon, droit
 * individuel du compte. FAIL-CLOSED : `modules` absent/nul ⇒ aucun droit.
 */
export function moduleOuvert(
  modules: readonly string[] | null | undefined,
  module: string,
): boolean {
  if (estModuleSuspendu(module)) return false
  if (estModuleInclus(module)) return true
  return (modules ?? []).includes(module)
}

// ═══ DEMANDE DE DIP/RSPC — ROBINET ROUVERT (13/07/2026) ═══
// HISTORIQUE. Le robinet avait été FERMÉ le 13/07/2026 au matin : le parcours de dépôt côté
// BD-DocPro était cassé — le paiement n'était jamais réclamé, et la demande déposée n'apparaissait
// pas dans l'espace client (la cliente recevait une référence… puis lisait « Aucun dossier »).
//
// CAUSE RÉELLE, trouvée le 13/07 (BD-DocPro, commits 6198a56 + 51d603f) : l'injecteur de jeton de
// BD-DocPro ne lisait le jeton que dans sessionStorage, alors que le magic link du dépôt l'écrit
// dans localStorage → aucun jeton injecté → /api/dossiers répondait 401 → espace client vide. Le
// paiement, dont le bouton vit DANS le dossier, devenait donc inatteignable : c'était une
// CONSÉQUENCE, pas une cause. Aucune donnée n'avait été perdue.
//
// RÉPARÉ ET DÉPLOYÉ côté BD-DocPro : jeton lu dans les deux stockages, redirection Stripe Checkout
// réclamée dès le dépôt (27 € HT, DIP technique), rattrapage possible depuis l'espace client.
// On ROUVRE donc le robinet : boutons « Demander un DIP/RSPC » visibles et routes serveur ouvertes.
//
// ⚠️ Le module cosmétique reste suspendu par ailleurs (MODULES_SUSPENDUS ci-dessus) : le bouton du
// lot cosmétique demeure donc inaccessible tant que ce module n'est pas rouvert. La demande de DIP
// part aujourd'hui de la fiche savon.
//
// Repasser ce drapeau à `true` suffit à refermer le robinet (front + serveur) sans supprimer de code.
export const DEMANDE_DIP_SUSPENDUE = false

// Quotas mensuels d'appels à l'API Claude par tier
export const CLAUDE_QUOTAS = {
  trial: 10, // pendant les 15 jours d'essai
  starter: 10,
  premium: 50,
} as const

// Durée de l'essai gratuit (jours)
export const TRIAL_DURATION_DAYS = 15

// Bandeau de fin d'essai sur l'accueil (27/10/2026) : affiché quand il reste au plus ce
// nombre de jours. Avant, rien ne prévenait : le mur n'apparaissait qu'après expiration.
export const ALERTE_FIN_ESSAI_JOURS = 5

// Sécurité connexion — protection anti-force-brute (audit du 09/07/2026, piste Aj1).
// Au-delà de LOGIN_MAX_ATTEMPTS échecs sur une fenêtre glissante de LOGIN_LOCK_WINDOW_MINUTES
// minutes (par e-mail), la connexion est temporairement refusée (HTTP 429). Un succès
// remet le compteur à zéro. Valeurs volontairement modérées : dissuader le brute-force
// sans gêner une savonnière qui se trompe quelques fois.
export const LOGIN_MAX_ATTEMPTS = 5
export const LOGIN_LOCK_WINDOW_MINUTES = 15

// BD-DocPro — application SÉPARÉE (autre dépôt, autre base) qui établit les dossiers
// réglementaires (DIP / RSPC). Instance UNIQUE (celle de Thérèse) ⇒ constante partagée, PAS un
// réglage par cliente : aucune migration, aucun champ à saisir.
export const URL_BD_DOCPRO = 'https://bd-docpro.netlify.app'

// Page de dépôt d'une demande de DIP (vérifiée en ligne le 13/07/2026 : HTTP 200). C'est là que
// l'abonnée glisse le fichier .json téléchargé depuis BD-SapoPro. Transport MANUEL assumé : les
// deux applications restent indépendantes (aucun couplage, aucune panne en cascade).
export const URL_DEPOT_DOCPRO = `${URL_BD_DOCPRO}/demander-dip`
